import { LINKED_PRODUCTS, type LinkedProduct } from "@rhema/client";

// A social link leaves the web app for Google, Apple or Microsoft and comes back to /link/callback.
// The product and link token wait in sessionStorage for that one tab. Storage can be missing or
// blocked, so every read and write is guarded.

const KEY = "rhema.link.pending";

type Pending = { product: LinkedProduct; linkToken: string };

export function rememberPendingLink(product: LinkedProduct, linkToken: string): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ product, linkToken }));
  } catch {
    // The callback then says it cannot finish, and the person starts again.
  }
}

/** Whether this tab started the link with this token. Keeps it for takePendingLink. */
export function hasPendingLink(linkToken: string): boolean {
  try {
    const pending = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as Partial<Pending> | null;
    return Boolean(linkToken) && pending?.linkToken === linkToken;
  } catch {
    return false;
  }
}

/** The product a link token belongs to, if this tab started it. Forgets it either way. */
export function takePendingLink(linkToken: string): LinkedProduct | null {
  let pending: Partial<Pending> | null = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as Partial<Pending> | null;
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!pending || pending.linkToken !== linkToken) return null;
  return LINKED_PRODUCTS.includes(pending.product as LinkedProduct)
    ? (pending.product as LinkedProduct)
    : null;
}
